import { Modal, Box, Grid } from '@sequencehq/core-components'
import { Footer } from './common/Footer'
import {
  GreyGrey60,
  GreyGrey80,
  Primary12Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { cubeUpdateShortcuts } from 'Cube/index'
import { match } from 'ts-pattern'
import { useNavigate } from 'react-router-dom'
import { useScheduleDates } from '../../../../../utils/useScheduleDates'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useScheduleHasFuturePhases } from '../../../../../utils/useHasFuturePhases'
import {
  ScheduleChangeDateSelector,
  ScheduleChangeDateType
} from 'common/components/ScheduleChangeDateSelector/ScheduleChangeDateSelector'

type FormData = {
  upgradeDateType: ScheduleChangeDateType
  customDate: Date | undefined
}

export const UpgradeScreen = (props: { onBack: () => void }) => {
  const { scheduleDates, formFields, isValid, onSubmit } = useUpgradeScreen()

  return (
    <>
      <Modal.Content>
        <Grid gridTemplateRows="auto auto auto" gap="12px">
          <Box {...Primary13SemiBold} color={GreyGrey80}>
            When should changes take effect?
          </Box>
          <ScheduleChangeDateSelector
            name="upgradeDateType"
            value={formFields.upgradeDateType.value}
            onChange={formFields.upgradeDateType.onChange}
            customDate={formFields.customDate.value}
            onCustomDateChange={formFields.customDate.onChange}
            scheduleDates={scheduleDates}
          />
          <Box color={GreyGrey60} {...Primary12Regular}>
            Creates a new phase where you can make price changes.
          </Box>
        </Grid>
      </Modal.Content>
      <Footer onContinue={onSubmit} onBack={props.onBack} disabled={!isValid} />
    </>
  )
}

const useUpgradeScreen = () => {
  const navigate = useNavigate()
  const billingScheduleId = useBillingScheduleId()
  const scheduleDatesQuery = useScheduleDates({
    billingScheduleId
  })
  const futurePhasesQuery = useScheduleHasFuturePhases({
    billingScheduleId
  })

  const form = useForm<FormData>({
    value: {
      upgradeDateType: futurePhasesQuery.hasFuturePhases
        ? 'customDate'
        : 'immediate',
      customDate: undefined
    },
    fieldConfiguration: [
      {
        property: 'upgradeDateType',
        validation: [required]
      },
      {
        property: 'customDate',
        disabled: ctx => ctx.formData.upgradeDateType !== 'customDate',
        validation: [required]
      }
    ]
  })

  const onSubmit = () => {
    if (!form.queries.isValid) {
      return
    }

    const startDate =
      match(form.queries.formData.upgradeDateType)
        .with('immediate', () => new Date())
        .with('nextBillingDate', () => scheduleDatesQuery.nextBillingDate)
        .with('customDate', () => form.queries.formData.customDate)
        .exhaustive() ?? new Date()

    const shortcutUuid = cubeUpdateShortcuts.set({
      type: 'upgrade',
      startDate: startDate
    })

    navigate(
      `/billing-schedules/${billingScheduleId}/edit?${cubeUpdateShortcuts.queryParam}=${shortcutUuid}`
    )
  }

  return {
    isLoading: scheduleDatesQuery.isLoading || futurePhasesQuery.isLoading,
    scheduleDates: {
      startDate: scheduleDatesQuery.startDate,
      endDate: scheduleDatesQuery.endDate,
      nextBillingDate: scheduleDatesQuery.nextBillingDate
    },
    hasFuturePhases: futurePhasesQuery.hasFuturePhases,
    formFields: form.fields,
    isValid: form.queries.isValid,
    onSubmit
  }
}
